import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import {
  getCurrentWorkflow,
  importWorkflow,
  getWorkflowHistory,
  deleteCurrentWorkflow,
} from '../api/workflows'
import {
  AlertCircle,
  Archive,
  CheckCircle2,
  Inbox,
  Trash2,
  X,
} from 'lucide-react'

export default function Workflows() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [workflowCode, setWorkflowCode] = useState('')
  const [workflowName, setWorkflowName] = useState('')
  const [workflowDescription, setWorkflowDescription] = useState('')
  const [showImportForm, setShowImportForm] = useState(false)

  // Fetch current workflow
  const { data: currentWorkflow, isLoading: isLoadingCurrent } = useQuery({
    queryKey: ['workflow', 'current'],
    queryFn: getCurrentWorkflow,
  })

  // Fetch workflow history
  const { data: history, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['workflow', 'history'],
    queryFn: getWorkflowHistory,
  })

  // Import mutation
  const importMutation = useMutation({
    mutationFn: importWorkflow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow'] })
      setWorkflowCode('')
      setWorkflowName('')
      setWorkflowDescription('')
      setShowImportForm(false)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteCurrentWorkflow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow'] })
    },
  })

  const handleImport = () => {
    if (!workflowCode.trim()) {
      alert(t('workflows.import.instructions'))
      return
    }

    importMutation.mutate({
      code: workflowCode,
      name: workflowName || undefined,
      description: workflowDescription || undefined,
    })
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete the current workflow? A backup will be created.')) {
      deleteMutation.mutate()
    }
  }

  if (isLoadingCurrent) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">{t('workflows.loading')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold text-white">{t('workflows.title')}</h2>
          <p className="text-muted-200 mt-1">{t('workflows.subtitle')}</p>
        </div>

        {!showImportForm && (
          <button
            onClick={() => setShowImportForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500/20 border border-primary-500/50 text-primary-100 hover:bg-primary-500/30 transition-colors"
          >
            <Inbox className="h-4 w-4" />
            {t('workflows.import.button')}
          </button>
        )}
      </div>

      {/* Import Form */}
      {showImportForm && (
        <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-100">{t('workflows.import.title')}</h3>
            <button
              onClick={() => setShowImportForm(false)}
              className="text-muted-300 hover:text-muted-100"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="space-y-4">
            {/* Name Input */}
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                Workflow Name <span className="text-muted-200">(optional)</span>
              </label>
              <input
                type="text"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                placeholder="e.g., Enology Expert Workflow v1"
                className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
              />
            </div>

            {/* Description Input */}
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                Description <span className="text-muted-200">(optional)</span>
              </label>
              <input
                type="text"
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                placeholder="e.g., Expert workflow for wine questions"
                className="w-full px-3 py-2 rounded-md border border-slate-700 bg-surface-300 text-slate-100 placeholder-slate-500 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
              />
            </div>

            {/* Code Textarea */}
            <div>
              <label className="block text-xs uppercase tracking-wide text-muted-300 mb-2">
                Workflow Code <span className="text-rose-300">*</span>
              </label>
              <div className="text-sm text-muted-200 mb-2">
                {t('workflows.import.instructions')}
              </div>
              <textarea
                value={workflowCode}
                onChange={(e) => setWorkflowCode(e.target.value)}
                placeholder={t('workflows.import.placeholder')}
                rows={15}
                className="w-full px-3 py-2 rounded-lg border border-slate-700 bg-slate-950 text-slate-100 placeholder-slate-600 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition font-mono text-sm"
              />
              <div className="text-xs text-muted-300 mt-1">
                {workflowCode.length} characters
              </div>
            </div>

            {/* Error Display */}
            {importMutation.error && (
              <div className="bg-rose-500/10 border border-rose-500/40 rounded-xl p-4">
                <h4 className="flex items-center gap-2 text-rose-200 font-medium">
                  <AlertCircle className="h-4 w-4" />
                  {t('workflows.import.error')}
                </h4>
                <p className="text-rose-300 text-sm mt-1">
                  {importMutation.error instanceof Error
                    ? importMutation.error.message
                    : t('common.unknownError')}
                </p>
              </div>
            )}

            {/* Success Display */}
            {importMutation.isSuccess && (
              <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-4">
                <h4 className="flex items-center gap-2 text-emerald-200 font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  {t('workflows.import.success')}
                </h4>
                <p className="text-emerald-200/80 text-sm mt-1">
                  Workflow imported successfully. Server will reload automatically.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleImport}
                disabled={importMutation.isPending || !workflowCode.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500/20 border border-primary-500/50 text-primary-100 hover:bg-primary-500/30 disabled:bg-slate-800 disabled:text-slate-500 disabled:border-slate-700 disabled:cursor-not-allowed transition-colors"
              >
                <Inbox className="h-4 w-4" />
                {importMutation.isPending ? t('workflows.import.importing') : t('workflows.import.button')}
              </button>
              <button
                onClick={() => setShowImportForm(false)}
                disabled={importMutation.isPending}
                className="px-4 py-2 rounded-lg bg-surface-300 border border-slate-700 text-slate-200 hover:bg-slate-800 transition-colors disabled:opacity-60"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Current Workflow Info */}
      <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">{t('workflows.current.title')}</h3>

        {currentWorkflow?.exists ? (
          <div className="space-y-4">
            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-300">Name</div>
                <div className="text-slate-100">{currentWorkflow.name || 'Unnamed'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-300">{t('workflows.current.imported')}</div>
                <div className="text-slate-100">
                  {currentWorkflow.imported_at
                    ? format(new Date(currentWorkflow.imported_at), 'MMM d, yyyy HH:mm')
                    : 'N/A'}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-300">Description</div>
                <div className="text-slate-100">{currentWorkflow.description || 'N/A'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-300">File Size</div>
                <div className="text-slate-100">
                  {currentWorkflow.file_size
                    ? `${(currentWorkflow.file_size / 1024).toFixed(1)} KB`
                    : 'N/A'}
                </div>
              </div>
            </div>

            {/* Code Preview */}
            {currentWorkflow.code_preview && (
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-300 mb-2">
                  {t('workflows.current.preview')}
                </div>
                <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-xs font-mono overflow-x-auto text-slate-100">
                  {currentWorkflow.code_preview}
                </pre>
              </div>
            )}

            {/* Delete Button */}
            <div className="pt-4 border-t border-slate-800">
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-200 hover:bg-rose-500/30 disabled:bg-slate-800 disabled:text-slate-500 disabled:border-slate-700 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                {deleteMutation.isPending ? t('common.delete') + '...' : t('common.delete')}
              </button>
              <div className="text-xs text-muted-200 mt-2">
                Server will fall back to default agent after deletion. A backup will be created.
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Archive className="mx-auto h-12 w-12 text-muted-300 mb-3" />
            <div className="text-slate-100 font-medium">{t('workflows.current.noWorkflow')}</div>
            <div className="text-muted-200 text-sm mt-1">
              {t('workflows.current.fallback')}
            </div>
          </div>
        )}
      </div>

      {/* Workflow History */}
      <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">{t('workflows.history.title')}</h3>

        {isLoadingHistory ? (
          <div className="text-center py-4 text-slate-400">{t('workflows.loading')}</div>
        ) : history && history.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-muted-200 uppercase tracking-wide">
                    Filename
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-muted-200 uppercase tracking-wide">
                    {t('workflows.history.date')}
                  </th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-muted-200 uppercase tracking-wide">
                    Size
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.filename} className="border-b border-slate-900/60">
                    <td className="py-2 px-3 text-sm text-slate-100 font-mono">
                      {item.filename}
                    </td>
                    <td className="py-2 px-3 text-sm text-muted-200">
                      {format(new Date(item.timestamp), 'MMM d, yyyy HH:mm:ss')}
                    </td>
                    <td className="py-2 px-3 text-sm text-muted-200">
                      {(item.file_size / 1024).toFixed(1)} KB
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4 text-slate-400">{t('workflows.history.noHistory')}</div>
        )}
      </div>
    </div>
  )
}
